package com.dotdrives.web.pages.settings

import com.dotdrives.controller.SettingsController
import com.dotdrives.model.OrgMember
import com.dotdrives.repository.SupabaseOrgRepository
import com.dotdrives.repository.SupabaseSettingsRepository
import com.dotdrives.web.shell.NavItem
import com.dotdrives.web.shell.PortalShell
import com.dotdrives.web.ui.useFlowState
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.cancel
import react.FC
import react.Props
import react.useEffect
import react.useState

private class SettingsControllerHolder(
    val scope: kotlinx.coroutines.CoroutineScope,
    val controller: SettingsController,
)

external interface SettingsPortalProps : Props {
    var userId: String
    var orgId: String
    var userInitials: String
    var userName: String
    var orgName: String
    /** owner|admin|member|field of the signed-in user in this org (AuthUiState.orgs' OrgSummary.role). */
    var currentUserRole: String
    /** Pauses disable operational settings; customer billing recovery uses its separate membership role. */
    var readOnly: Boolean
    var supportSession: Boolean
    var onFiscalSaved: (String, Int, Int) -> Unit
    var onSwitchOrg: () -> Unit
    var onNavigate: (NavItem) -> Unit
    var onSignOut: () -> Unit
}

val SettingsPortal = FC<SettingsPortalProps> { props ->
    val (holder) = useState {
        val scope = MainScope()
        SettingsControllerHolder(
            scope = scope,
            controller = SettingsController(
                orgRepo = SupabaseOrgRepository(),
                settingsRepo = SupabaseSettingsRepository(),
                scope = scope,
                orgId = props.orgId,
                onFiscalSaved = props.onFiscalSaved,
            ),
        )
    }
    val state = useFlowState(holder.controller.state)
    val readOnly = props.asDynamic().readOnly == true
    val effectiveRole = if (readOnly) "member" else props.currentUserRole
    val workflow = com.dotdrives.web.ui.useWorkflowController(props.userId, props.orgId, effectiveRole, "team")
    val workflowState = useFlowState(workflow.state)

    useEffect(holder) {
        try {
            awaitCancellation()
        } finally {
            holder.scope.cancel()
        }
    }

    val canManageUsers = com.dotdrives.model.canAdminister(effectiveRole)

    PortalShell {
        active = NavItem.Settings
        onNavigate = props.onNavigate
        userInitials = props.userInitials
        userName = props.userName
        orgName = props.orgName
        onSwitchOrg = props.onSwitchOrg
        onSignOut = props.onSignOut

        SettingsPage {
            orgId = props.orgId
            userId = props.userId
            workflowController = workflow
            this.workflowState = workflowState
            controller = holder.controller
            this.state = state
            members = state.members
            currentUserRole = effectiveRole
            billingRole = if (props.asDynamic().supportSession == true) "member" else props.currentUserRole
            this.canManageUsers = canManageUsers
        }
    }
}
